/** Infra nodes (physical VPS) — tab inside Servers page. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../components/ToastProvider';
import { api } from '../services/api';
import type { InfraDonorItem, InfraJobItem, InfraLocationItem, InfraNodeItem } from '../services/types';
import { notifyApiResult } from '../utils/notify';

const emptyForm = {
  host: '',
  ssh_port: 22,
  ssh_login: 'root',
  ssh_password: '',
  location: '',
  node_number: 1,
  domain: '',
  base_url: '',
};

type FormState = typeof emptyForm;

function locationLabel(loc: InfraLocationItem): string {
  const country = loc.country ? ` — ${loc.country}` : '';
  const off = loc.enabled ? '' : ' (выкл)';
  return `${loc.code}${country}${off}`;
}

export function InfraNodesPanel({ canWrite }: { canWrite: boolean }) {
  const toast = useToast();
  const [nodes, setNodes] = useState<InfraNodeItem[]>([]);
  const [locations, setLocations] = useState<InfraLocationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [job, setJob] = useState<InfraJobItem | null>(null);
  const [donors, setDonors] = useState<InfraDonorItem[]>([]);
  const [donorId, setDonorId] = useState<number | ''>('');
  const [busy, setBusy] = useState(false);
  const pollRef = useRef<number | null>(null);

  const load = useCallback(
    async (background = false) => {
      if (!background) setLoading(true);
      setError(null);
      const [nodesRes, locsRes] = await Promise.all([api.listInfraNodes(), api.listInfraLocations()]);
      if (nodesRes.data) setNodes(nodesRes.data);
      else if (nodesRes.error) {
        setError(nodesRes.error.message);
        if (!background) toast.error(nodesRes.error.message);
      }
      if (locsRes.data) setLocations(locsRes.data);
      if (!background) setLoading(false);
    },
    [toast],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const stopPoll = () => {
    if (pollRef.current != null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => () => stopPoll(), []);

  const watchJob = (jobId: number, silent = false) => {
    stopPoll();
    setBusy(true);
    const tick = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      const res = await api.getInfraJob(jobId);
      if (res.data) {
        setJob(res.data);
        if (res.data.status === 'succeeded' || res.data.status === 'failed') {
          stopPoll();
          setBusy(false);
          await load(true);
          if (res.data.status === 'succeeded') toast.success('Операция завершена');
          else toast.error(res.data.error || 'Операция завершилась с ошибкой');
        }
      } else if (res.error) {
        stopPoll();
        setBusy(false);
        if (!silent) toast.error(res.error.message);
      }
    };
    void tick();
    pollRef.current = window.setInterval(() => void tick(), 2000);
  };

  useEffect(() => {
    if (selectedId == null) return;
    let cancelled = false;
    void (async () => {
      const res = await api.getInfraActiveJob(selectedId);
      if (cancelled || !res.data) return;
      setJob(res.data);
      watchJob(res.data.id, true);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const selected = nodes.find((n) => n.id === selectedId) || null;

  const defaultLocation = () => locations[0]?.code || '';

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm, location: defaultLocation(), node_number: 1 });
    setShowForm(true);
  };

  const openEdit = (n: InfraNodeItem) => {
    setEditingId(n.id);
    setForm({
      host: n.host,
      ssh_port: n.ssh_port,
      ssh_login: n.ssh_login,
      ssh_password: '',
      location: n.location,
      node_number: n.node_number || 1,
      domain: n.domain || '',
      base_url: n.base_url || '',
    });
    setShowForm(true);
  };

  const submitForm = async () => {
    if (!form.host.trim() || !form.ssh_login.trim()) {
      toast.error('Host и SSH login обязательны');
      return;
    }
    if (!form.location.trim()) {
      toast.error('Выберите Location (регион из списка)');
      return;
    }
    const nodeNumber = Number(form.node_number) || 0;
    if (nodeNumber < 1) {
      toast.error('Node number должен быть ≥ 1 (1 = primary для Sync replicas)');
      return;
    }
    if (editingId == null && !form.ssh_password) {
      toast.error('Укажите SSH password');
      return;
    }
    const domain = form.domain.trim();
    const baseUrl = form.base_url.trim();
    setBusy(true);
    if (editingId == null) {
      const res = await api.createInfraNode({
        host: form.host.trim(),
        ssh_port: Number(form.ssh_port) || 22,
        ssh_login: form.ssh_login.trim(),
        ssh_password: form.ssh_password,
        location: form.location.trim(),
        node_number: nodeNumber,
        domain: domain || undefined,
        base_url: baseUrl || undefined,
      });
      if (notifyApiResult(toast, res, () => 'Нода добавлена, идёт SSH-проверка')) {
        setShowForm(false);
        await load(true);
        if (res.data) {
          setSelectedId(res.data.id);
          const check = await api.checkInfraNode(res.data.id);
          if (check.data) {
            watchJob(check.data.job_id);
            return;
          }
          if (check.error) toast.error(check.error.message);
        }
      }
    } else {
      const payload: Record<string, unknown> = {
        host: form.host.trim(),
        ssh_port: Number(form.ssh_port) || 22,
        ssh_login: form.ssh_login.trim(),
        location: form.location.trim(),
        node_number: nodeNumber,
        domain: domain || null,
        base_url: baseUrl || null,
      };
      if (form.ssh_password.trim()) payload.ssh_password = form.ssh_password;
      const res = await api.updateInfraNode(editingId, payload);
      if (notifyApiResult(toast, res, () => 'Нода обновлена')) {
        setShowForm(false);
        await load(true);
      }
    }
    setBusy(false);
  };

  const importFromEnv = async () => {
    if (
      !window.confirm(
        'Импортировать все записи из WG_SERVERS (.env) в БД?\n' +
          'Для каждой location после импорта runtime будет брать ноды из БД, а не из env.',
      )
    ) {
      return;
    }
    setBusy(true);
    const res = await api.importInfraFromWgServers();
    if (
      notifyApiResult(
        toast,
        res,
        (data) => `Импорт: +${data.imported} новых, ${data.updated} обновлено, ${data.skipped} пропущено`,
      )
    ) {
      await load(true);
    }
    setBusy(false);
  };

  const removeNode = async (n: InfraNodeItem) => {
    if (!window.confirm(`Удалить ноду ${n.host}?`)) return;
    const res = await api.deleteInfraNode(n.id);
    if (notifyApiResult(toast, res, () => 'Нода удалена')) {
      if (selectedId === n.id) setSelectedId(null);
      await load(true);
    }
  };

  const runJob = async (fn: () => ReturnType<typeof api.checkInfraNode>) => {
    setBusy(true);
    setJob(null);
    const res = await fn();
    if (res.data) watchJob(res.data.job_id);
    else {
      setBusy(false);
      if (res.error) toast.error(res.error.message);
    }
  };

  const loadDonors = async (nodeId: number) => {
    const res = await api.listInfraDonors(nodeId);
    if (res.data) {
      setDonors(res.data);
      setDonorId(res.data[0]?.id ?? '');
    }
  };

  useEffect(() => {
    if (selectedId != null) void loadDonors(selectedId);
  }, [selectedId]);

  const facts = selected?.facts || {};

  const locationOptionsForEdit = (() => {
    if (!editingId) return locations;
    const current = form.location;
    if (current && !locations.some((l) => l.code === current)) {
      return [{ code: current, country: null, enabled: false }, ...locations];
    }
    return locations;
  })();

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        {canWrite && (
          <>
            <button type="button" className="btn btn-primary" onClick={openCreate} disabled={!locations.length}>
              Добавить сервер
            </button>
            <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => void importFromEnv()}>
              Импорт из WG_SERVERS
            </button>
          </>
        )}
        <button type="button" className="btn btn-ghost" disabled={loading} onClick={() => void load()}>
          Обновить список
        </button>
      </div>

      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}
      {!loading && locations.length === 0 && (
        <p style={{ color: 'var(--danger)' }}>
          Нет регионов в таблице servers — сначала добавьте регион на вкладке «Регионы».
        </p>
      )}
      {loading && nodes.length === 0 && <p style={{ color: 'var(--text-muted)' }}>Загрузка…</p>}
      {!loading && nodes.length === 0 && locations.length > 0 && (
        <p style={{ color: 'var(--text-muted)' }}>
          Нод пока нет. Добавьте VPS или нажмите «Импорт из WG_SERVERS».
        </p>
      )}

      {nodes.length > 0 && (
        <div className="table-wrap" style={{ marginBottom: '1rem' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Node</th>
                <th>Host</th>
                <th>Location</th>
                <th>URL</th>
                <th>OS</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {nodes.map((n) => (
                <tr
                  key={n.id}
                  onClick={() => setSelectedId(n.id)}
                  style={{
                    cursor: 'pointer',
                    background: selectedId === n.id ? 'var(--accent-soft)' : undefined,
                  }}
                >
                  <td>
                    <code>
                      {n.location.toUpperCase()}-{n.node_number}
                    </code>
                    {n.node_number === 1 ? ' · primary' : ''}
                  </td>
                  <td>
                    <code>{n.host}</code>:{n.ssh_port}
                  </td>
                  <td>{n.location}</td>
                  <td>
                    <code style={{ fontSize: '0.75rem' }}>{n.base_url || '—'}</code>
                  </td>
                  <td>{n.os || '—'}</td>
                  <td>{n.status}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                      <button type="button" className="btn btn-ghost" onClick={() => setSelectedId(n.id)}>
                        Карточка
                      </button>
                      {canWrite && (
                        <>
                          <button type="button" className="btn btn-ghost" onClick={() => openEdit(n)}>
                            Изменить
                          </button>
                          <button type="button" className="btn btn-ghost" onClick={() => void removeNode(n)}>
                            Удалить
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h3 style={{ marginTop: 0 }}>{editingId == null ? 'Новая нода' : `Редактирование #${editingId}`}</h3>
          <div className="form-grid" style={{ display: 'grid', gap: '0.75rem', maxWidth: 520 }}>
            {(
              [
                ['host', 'Host / IP'],
                ['ssh_port', 'SSH Port'],
                ['ssh_login', 'SSH Login'],
                ['ssh_password', editingId ? 'SSH Password (пусто = не менять)' : 'SSH Password'],
              ] as const
            ).map(([key, label]) => (
              <label key={key} style={{ display: 'grid', gap: 4 }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{label}</span>
                <input
                  className="input"
                  type={key === 'ssh_password' ? 'password' : key === 'ssh_port' ? 'number' : 'text'}
                  value={String(form[key])}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                />
              </label>
            ))}
            <label style={{ display: 'grid', gap: 4 }}>
              <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Location (регион)</span>
              <select
                className="input"
                value={form.location}
                onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
              >
                <option value="" disabled>
                  Выберите регион…
                </option>
                {locationOptionsForEdit.map((loc) => (
                  <option key={loc.code} value={loc.code}>
                    {locationLabel(loc)}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: 'grid', gap: 4 }}>
              <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                Node number (1 = primary)
              </span>
              <input
                className="input"
                type="number"
                min={1}
                value={form.node_number}
                onChange={(e) => setForm((f) => ({ ...f, node_number: Number(e.target.value) || 1 }))}
              />
            </label>
            <label style={{ display: 'grid', gap: 4 }}>
              <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                Easy-WG base URL (пусто = авто из location+number + WG_EASY_DOMAIN)
              </span>
              <input
                className="input"
                type="text"
                value={form.base_url}
                onChange={(e) => setForm((f) => ({ ...f, base_url: e.target.value }))}
                placeholder="https://ru1.wireguard.example.com"
              />
            </label>
            <label style={{ display: 'grid', gap: 4 }}>
              <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Domain (опционально)</span>
              <input
                className="input"
                type="text"
                value={form.domain}
                onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value }))}
                placeholder="ru1.wireguard.example.com"
              />
            </label>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void submitForm()}>
              Сохранить
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setShowForm(false)}>
              Отмена
            </button>
          </div>
        </div>
      )}

      {selected && (
        <div className="card infra-node-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            <div>
              <h3 style={{ margin: '0 0 0.35rem' }}>
                <code>
                  {selected.location.toUpperCase()}-{selected.node_number}
                </code>
                <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> · {selected.host}</span>
              </h3>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                {selected.status}
                {selected.os ? ` · ${selected.os}` : ''}
                {selected.base_url ? ` · ${selected.base_url}` : ''}
              </div>
            </div>
            {canWrite && (
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={busy}
                  onClick={() => void runJob(() => api.checkInfraNode(selected.id))}
                >
                  Check / Refresh
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={busy}
                  onClick={() => {
                    if (!window.confirm('Установить Docker + Easy-WG на этой ноде?')) return;
                    void runJob(() => api.installWgInfraNode(selected.id));
                  }}
                >
                  Установить Easy-WG
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={busy}
                  onClick={() => {
                    if (!window.confirm("Provision = установка Easy-WG (без HTTPS/Let's Encrypt)?")) return;
                    void runJob(() => api.provisionInfraNode(selected.id));
                  }}
                >
                  Provision
                </button>
              </div>
            )}
          </div>

          <div className="infra-facts" style={{ marginTop: '1rem' }}>
            {(
              [
                ['HOSTNAME', 'Hostname'],
                ['UPTIME', 'Uptime'],
                ['KERNEL', 'Kernel'],
                ['CPU_CORES', 'CPU'],
                ['LOADAVG', 'Load'],
                ['RAM', 'RAM'],
                ['DISK', 'Disk'],
                ['DOCKER', 'Docker'],
                ['COMPOSE', 'Compose'],
                ['PUBLIC_IP', 'Public IP'],
                ['WG_EASY', 'WG-Easy'],
              ] as const
            ).map(([key, label]) => (
              <div key={key} className="infra-fact-row">
                <span className="muted">{label}</span>
                <strong>{facts[key] || '—'}</strong>
              </div>
            ))}
          </div>

          {canWrite && (
            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <select
                className="input"
                style={{ maxWidth: 280 }}
                value={donorId}
                onChange={(e) => setDonorId(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">Sync from… (та же location)</option>
                {donors.map((d) => (
                  <option key={d.id} value={d.id}>
                    #{d.id} {d.host} ({d.location.toUpperCase()}-{d.node_number ?? '?'})
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={busy || donorId === ''}
                onClick={() => {
                  if (donorId === '') return;
                  if (!window.confirm(`Синхронизировать wg-easy с ноды #${donorId}?`)) return;
                  void runJob(() => api.syncInfraNode(selected.id, Number(donorId)));
                }}
              >
                Sync from…
              </button>
            </div>
          )}

          {job && (
            <div style={{ marginTop: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: '0.875rem' }}>
                  Job #{job.id} · {job.job_type} · <strong>{job.status}</strong>
                </span>
                <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>{job.progress}%</span>
              </div>
              <div
                style={{
                  height: 6,
                  borderRadius: 4,
                  background: 'var(--border)',
                  overflow: 'hidden',
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    width: `${job.progress}%`,
                    height: '100%',
                    background: job.status === 'failed' ? 'var(--danger)' : 'var(--accent)',
                  }}
                />
              </div>
              <pre className="infra-job-log">{(job.log_text || '…').slice(-120000)}</pre>
              {job.error && <p style={{ color: 'var(--danger)', fontSize: '0.875rem' }}>{job.error}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
